import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { EllipsisTooltipDirective } from '../../shared/ellipsis-tooltip.directive';
import { TableScrollSyncDirective } from '../../shared/table-scroll-sync.directive';
import { AlertCustomComponent } from 'pharedata-ng-components';
import { GlobalState } from '../../services/global-state';
import { LlamanosApiService } from '../../services/api-llamanos.service';
import { ExternalApisService } from '../../services/external-apis.service';
import { getCategoriasSiniestro } from '../../utils/categorias';
import { getPeriodosDesde } from '../../utils/timeZone';
import { ESTADOS_CASOS, formatFecha } from '../../utils/utils';
import { VerCasoComponent } from '../../shared/ver-caso/ver-caso.component';

@Component({
  selector: 'app-historial-siniestro',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    EllipsisTooltipDirective,
    TableScrollSyncDirective,
    VerCasoComponent,
    AlertCustomComponent,
  ],
  templateUrl: './historial-siniestro.component.html',
  styleUrl: './historial-siniestro.component.scss',
})
export class HistorialSiniestroComponent implements OnInit {
  formatFecha = formatFecha;
  // Datos para Estación de Servicio
  edsOptions: Array<{ label: string; value: number; desde: Date }> = [];

  // Datos para Período
  periodos: Array<{ name: string; id: string }> = [];

  // Variables para los valores seleccionados
  selectedEstacion: number | null = null;
  selectedPeriodo: any = null;
  searchTerm: string = '';
  casos: any = [];
  private casosOriginales: any = []; // Casos sin filtrar por periodo
  private categoriasRaw: Record<string, any> = {}; // Para validación en onVerCaso
  showVerCaso: boolean = false;
  casoSeleccionado: any = null;
  subcategoriaCompleta: any = null;
  showAlert: boolean = false;
  alertContent: string = '';
  alertIcon: string = 'success';
  ESTADOS_CASOS: any = ESTADOS_CASOS;

  isArcoprime: boolean = false;
  pagadores: Array<{ id_pagador: string; label: string }> = [];
  selectedPagador: string | null = null;
  hasSearched: boolean = false;

  constructor(
    private globalState: GlobalState,
    private api: LlamanosApiService,
    private externalApis: ExternalApisService,
  ) {}

  ngOnInit(): void {
    this.globalState.isArcoprime$.subscribe((v) => (this.isArcoprime = v));

    this.globalState.pagadores$.subscribe((pagadores) => {
      if (!this.isArcoprime) return;
      this.pagadores = pagadores || [];
      if (this.pagadores.length === 1) {
        this.onPagadorChange(this.pagadores[0].id_pagador);
      }
    });

    // Cargar opciones de estaciones desde GlobalState (no arcoprime)
    this.globalState.allEds$.subscribe((eds) => {
      if (this.isArcoprime) return;
      this.edsOptions = eds || [];
      if (this.edsOptions.length === 1) {
        this.selectedEstacion = this.edsOptions[0]?.value;
        this.updatePeriodos();
        this.loadCasos();
      }
    });

    this.api.getCategorias('Siniestro').subscribe({
      next: (data) => {
        const filteredSource = getCategoriasSiniestro(data);
        this.categoriasRaw = filteredSource;

        // Recalcular hasValidCategory para casos ya cargados
        if (this.casosOriginales.length > 0) {
          this.casosOriginales.forEach((caso: any) => {
            caso.hasValidCategory = this.checkValidCategoryAndSubcategory(caso);
          });
          this.applyFilters();
        }
      },
      error: (err) => {
        console.error(
          'Error cargando categorias en historial-siniestro: ',
          err,
        );
      },
    });
  }

  onPagadorChange(idPagador: string): void {
    this.selectedPagador = idPagador;
    this.selectedEstacion = null;
    this.selectedPeriodo = null;
    this.edsOptions = [];
    this.periodos = [];
    this.casos = [];
    this.casosOriginales = [];
    this.hasSearched = false;
    if (!idPagador) return;
    this.globalState.pagador = idPagador;
    this.externalApis.getEdsForPagador(idPagador, true).subscribe((eds) => {
      this.edsOptions = eds || [];
      if (this.edsOptions.length === 1) {
        this.selectedEstacion = this.edsOptions[0]?.value;
        this.updatePeriodos();
        this.loadCasos();
      }
    });
  }

  loadCasos(): void {
    if (this.selectedEstacion) {
      this.showAlert = false;
      // Obtener la estación completa para acceder a la propiedad 'desde'
      const estacionCompleta = this.edsOptions.find(
        (eds) => eds.value === this.selectedEstacion,
      );

      if (!estacionCompleta || !estacionCompleta.desde) {
        this.casos = [];
        return;
      }

      this.hasSearched = true;
      this.api.getCaso(this.selectedEstacion).subscribe({
        next: (data) => {
          // Filtrar casos que tengan fechaCreacionCaso >= desde de la estación
          // y categoria == Siniestro
          const casosFiltrados = (data.records || []).filter((record: any) => {
            if (!record.FechaCreacion__c) return false;
            if (record.Categoria1__c != 'Siniestro') return false;

            const iso = record.FechaCreacion__c.replace('+0000', 'Z');
            const fechaCreacionCaso = new Date(iso);

            // Comparar fechas (sin horas, solo fecha)
            const fechaDesde = new Date(estacionCompleta.desde);
            fechaDesde.setHours(0, 0, 0, 0);
            const fechaCaso = new Date(fechaCreacionCaso);
            fechaCaso.setHours(0, 0, 0, 0);

            return fechaCaso >= fechaDesde;
          });

          // Calcular y asignar hasValidCategory a cada caso
          casosFiltrados.forEach((caso: any) => {
            caso.hasValidCategory = this.checkValidCategoryAndSubcategory(caso);
          });

          // Guardar casos originales (filtrados por fecha desde)
          this.casosOriginales = casosFiltrados;
          // Aplicar filtro de periodo si hay uno seleccionado
          this.applyFilters();
        },
        error: (err) => {
          this.alertContent =
            'Error en servicio Salesforce, por favor inténtalo de nuevo.';
          this.alertIcon = 'error';
          this.showAlert = true;
          console.error('Error cargando casos:', err);
        },
      });
    }
  }

  closeAlert = () => {
    this.showAlert = false;
  };

  hideGreyBackground = () => {};
  showGreyBackground = () => {};
  scrollTo = (_x: number, _y: number) => {};

  onPeriodoChange(periodoId: string | null): void {
    this.selectedPeriodo = periodoId;
    this.applyFilters();
  }

  applyFilters(): void {
    let casosFiltrados = [...this.casosOriginales];

    // Filtrar por periodo si está seleccionado
    if (this.selectedPeriodo) {
      casosFiltrados = casosFiltrados.filter((caso: any) => {
        if (!caso.FechaCreacion__c) return false;

        const iso = caso.FechaCreacion__c.replace('+0000', 'Z');
        const fechaCreacionCaso = new Date(iso);
        const añoCaso = fechaCreacionCaso.getFullYear();
        const mesCaso = fechaCreacionCaso.getMonth() + 1; // getMonth() retorna 0-11
        const periodoCaso = `${añoCaso}${String(mesCaso).padStart(2, '0')}`;

        return periodoCaso === this.selectedPeriodo;
      });
    }

    // Filtrar por término de búsqueda
    if (this.searchTerm && this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase().trim();
      casosFiltrados = casosFiltrados.filter((caso: any) => {
        return (
          String(caso.CaseNumber || '')
            .toLowerCase()
            .includes(searchLower) ||
          String(caso.Categoria1__c || '')
            .toLowerCase()
            .includes(searchLower) ||
          String(caso.Categoria2__c || '')
            .toLowerCase()
            .includes(searchLower) ||
          String(caso.Status || '')
            .toLowerCase()
            .includes(searchLower) ||
          String(caso.Origin || '')
            .toLowerCase()
            .includes(searchLower)
        );
      });
    }

    this.casos = casosFiltrados;
  }

  onVerCaso(caso: any): void {
    const categoria2 = caso?.Categoria2__c;

    const categoriaEntry = this.categoriasRaw?.['Siniestro'];

    const rawSubcats: any[] = categoriaEntry;
    for (const sub of rawSubcats) {
      if (!sub || typeof sub !== 'object') continue;
      const entries = Object.entries(sub);
      if (entries.length === 0) continue;
      const [subKey, subcategoriaData] = entries[0];
      if (subKey === categoria2) {
        this.subcategoriaCompleta = {
          key: subKey,
          label: subKey,
          ...(subcategoriaData as any),
        };
        break;
      }
    }

    if (!this.subcategoriaCompleta) {
      this.subcategoriaCompleta = {
        key: categoria2,
        label: categoria2,
      };
    }

    this.casoSeleccionado = caso;
    this.showVerCaso = true;
  }

  // Private method to check if a case has valid category and subcategory
  private checkValidCategoryAndSubcategory(caso: any): boolean {
    const categoria2 = caso?.Categoria2__c;

    if (!categoria2) {
      return false;
    }

    const categoriaEntry = this.categoriasRaw?.['Siniestro'];
    const existsCategoria = Array.isArray(categoriaEntry);

    if (!existsCategoria) {
      return false;
    }

    const rawSubcats: any[] = categoriaEntry;
    for (const sub of rawSubcats) {
      if (!sub || typeof sub !== 'object') continue;
      const entries = Object.entries(sub);
      if (entries.length === 0) continue;
      const [subKey] = entries[0];
      if (subKey === categoria2) {
        return true;
      }
    }

    return false;
  }

  onCloseVerCaso(): void {
    this.showVerCaso = false;
    this.casoSeleccionado = null;
    this.subcategoriaCompleta = null;
  }

  get selectedEstacionComplete(): {
    label: string;
    value: number;
    desde: Date;
  } | null {
    if (!this.selectedEstacion) return null;
    return (
      this.edsOptions.find((eds) => eds.value === this.selectedEstacion) || null
    );
  }

  onEstacionChange(estacionValue: number): void {
    this.selectedEstacion = estacionValue;
    this.updatePeriodos();
    this.loadCasos();
  }

  private updatePeriodos(): void {
    if (!this.selectedEstacion) {
      this.periodos = [];
      return;
    }

    const estacionCompleta = this.edsOptions.find(
      (eds) => eds.value === this.selectedEstacion,
    );

    if (!estacionCompleta || !estacionCompleta.desde) {
      this.periodos = [];
      return;
    }

    this.periodos = getPeriodosDesde(estacionCompleta.desde);
  }
}
